import { useState, useEffect, useCallback } from 'react';
import RetailerLayout from './RetailerLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Database, 
  Plus, 
  Upload, 
  Search, 
  Edit, 
  Trash2, 
  Download,
  RefreshCw,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  Package
} from 'lucide-react';
import api from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import BulkPostModal from './BulkPostModal';
import { DISCOUNT_TIERS } from '../../utils/discountTiers';

const CATEGORIES = [
  "Bakery & Bread",
  "Beverages",
  "Breakfast & Cereal",
  "Dairy & Eggs",
  "Deli & Prepared Foods",
  "Frozen Foods",
  "Fruits",
  "Health & Nutrition",
  "Household Essentials",
  "Meat & Poultry",
  "Miscellaneous",
  "Oils, Sauces & Spices",
  "Pantry Staples",
  "Pasta, Rice & Grains",
  "Personal Care",
  "Pet Supplies",
  "Baby & Kids",
  "Seafood",
  "Snacks & Candy",
  "Vegetables"
];

const STORE_DISCOUNT_OPTIONS = [10, 15, 20, 25, 30, 35, 40, 45, 50];

// Map a store-registry default discount (a raw % off) to the closest live
// pricing tier level (1-3) so bulk posts default to the grocer's intent.
const mapStoredDiscountToTierLevel = (pct) => {
  if (pct === null || pct === undefined) return null;
  let best = null;
  let bestDiff = Infinity;
  DISCOUNT_TIERS.forEach((tier) => {
    const diff = Math.abs(tier.consumer - pct);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = tier.level;
    }
  });
  return best;
};

export default function StoreCodeRegistry({ user, onLogout }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_codes: 0, categories: {} });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    barcode: '',
    product_name: '',
    brand: '',
    category: '',
    unit_size: '',
    default_discount_tier: null,
    notes: ''
  });
  
  // Bulk upload states
  const [csvText, setCsvText] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [overwriteDuplicates, setOverwriteDuplicates] = useState(false);

  // Bulk post (list selected codes as live deals)
  const [selectedBarcodes, setSelectedBarcodes] = useState(new Set());
  const [showBulkPost, setShowBulkPost] = useState(false);
  
  const { t } = useLanguage();

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      if (searchQuery) params.append('query', searchQuery);
      if (filterCategory && filterCategory !== 'all') params.append('category', filterCategory);
      
      const response = await api.get(`/store-codes/list?${params}`);
      if (response.data.success) {
        setEntries(response.data.entries);
        setTotalPages(response.data.pages);
        setSelectedBarcodes(new Set());
      }
    } catch (error) {
      console.error('Failed to fetch store codes:', error);
      toast.error(t('common.error', 'Failed to load store codes'));
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, filterCategory, t]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/store-codes/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [fetchEntries, fetchStats]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEntries();
  };

  const resetForm = () => {
    setFormData({
      barcode: '',
      product_name: '',
      brand: '',
      category: '',
      unit_size: '',
      default_discount_tier: null,
      notes: ''
    });
  };

  const handleAddEntry = async () => {
    if (!formData.barcode || !formData.product_name || !formData.category) {
      toast.error(t('store_codes.required_fields', 'Barcode, Product Name, and Category are required'));
      return;
    }

    try {
      const response = await api.post('/store-codes/create', formData);
      if (response.data.success) {
        toast.success(t('store_codes.created', 'Store code created successfully'));
        setShowAddDialog(false);
        resetForm();
        fetchEntries();
        fetchStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error', 'Failed to create store code'));
    }
  };

  const handleEditEntry = async () => {
    if (!selectedEntry) return;

    try {
      const response = await api.put(`/store-codes/update/${selectedEntry.id}`, {
        product_name: formData.product_name,
        brand: formData.brand || null,
        category: formData.category,
        unit_size: formData.unit_size || null,
        default_discount_tier: formData.default_discount_tier,
        notes: formData.notes || null
      });
      if (response.data.success) {
        toast.success(t('store_codes.updated', 'Store code updated successfully'));
        setShowEditDialog(false);
        setSelectedEntry(null);
        resetForm();
        fetchEntries();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error', 'Failed to update store code'));
    }
  };

  const handleDeleteEntry = async () => {
    if (!selectedEntry) return;

    try {
      const response = await api.delete(`/store-codes/delete/${selectedEntry.id}`);
      if (response.data.success) {
        toast.success(t('store_codes.deleted', 'Store code deleted successfully'));
        setShowDeleteDialog(false);
        setSelectedEntry(null);
        fetchEntries();
        fetchStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error', 'Failed to delete store code'));
    }
  };

  const openEditDialog = (entry) => {
    setSelectedEntry(entry);
    setFormData({
      barcode: entry.barcode,
      product_name: entry.product_name,
      brand: entry.brand || '',
      category: entry.category,
      unit_size: entry.unit_size || '',
      default_discount_tier: entry.default_discount_tier,
      notes: entry.notes || ''
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (entry) => {
    setSelectedEntry(entry);
    setShowDeleteDialog(true);
  };

  const toggleSelection = (barcode) => {
    setSelectedBarcodes((prev) => {
      const next = new Set(prev);
      if (next.has(barcode)) {
        next.delete(barcode);
      } else {
        next.add(barcode);
      }
      return next;
    });
  };

  const allOnPageSelected =
    entries.length > 0 && entries.every((e) => selectedBarcodes.has(e.barcode));

  const toggleSelectAll = () => {
    setSelectedBarcodes((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        entries.forEach((e) => next.delete(e.barcode));
      } else {
        entries.forEach((e) => next.add(e.barcode));
      }
      return next;
    });
  };

  const getSelectedProducts = () =>
    entries
      .filter((e) => selectedBarcodes.has(e.barcode))
      .map((e) => ({
        barcode: e.barcode,
        product_name: e.product_name,
        category: e.category,
        brand: e.brand,
        unit_size: e.unit_size,
        regular_price: null,
        discount_level: mapStoredDiscountToTierLevel(e.default_discount_tier),
      }));

  const handleBulkPostSuccess = () => {
    setShowBulkPost(false);
    setSelectedBarcodes(new Set());
    fetchEntries();
  };

  const parseCsvAndUpload = async () => {
    if (!csvText.trim()) {
      toast.error(t('store_codes.empty_csv', 'Please paste CSV data'));
      return;
    }

    // Parse CSV
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      toast.error(t('store_codes.invalid_csv', 'CSV must have a header row and at least one data row'));
      return;
    }

    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    const requiredHeaders = ['barcode', 'product_name', 'category'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
      return;
    }

    const entries = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length < headers.length) continue;

      const entry = {};
      headers.forEach((header, idx) => {
        const value = values[idx];
        if (header === 'default_discount_tier') {
          entry[header] = value ? parseInt(value, 10) : null;
        } else {
          entry[header] = value || null;
        }
      });

      if (entry.barcode && entry.product_name && entry.category) {
        entries.push(entry);
      }
    }

    if (entries.length === 0) {
      toast.error(t('store_codes.no_valid_rows', 'No valid rows found in CSV'));
      return;
    }

    try {
      const response = await api.post('/store-codes/bulk-upload', {
        entries,
        overwrite_duplicates: overwriteDuplicates
      });
      
      if (response.data.success) {
        setUploadResult(response.data.summary);
        toast.success(`Uploaded: ${response.data.summary.created} created, ${response.data.summary.updated} updated`);
        fetchEntries();
        fetchStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error', 'Bulk upload failed'));
    }
  };

  const downloadTemplate = () => {
    const template = `barcode,product_name,brand,category,unit_size,default_discount_tier,notes
SKU001,Example Product,Brand Name,Fruits,16 oz,20,Optional notes
SKU002,Another Product,,Vegetables,1 lb,25,`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'store_codes_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <RetailerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Database className="w-6 h-6 text-blue-600" />
              {t('store_codes.title', 'Store Code Registry')}
            </h1>
            <p className="text-gray-600 mt-1">
              {t('store_codes.subtitle', 'Manage proprietary barcode mappings for your store')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowBulkPost(true)}
              disabled={selectedBarcodes.size === 0}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
              data-testid="post-selected-btn"
            >
              <Package className="w-4 h-4 mr-2" />
              {t('store_codes.post_selected', 'Post Selected')}
              {selectedBarcodes.size > 0 ? ` (${selectedBarcodes.size})` : ''}
            </Button>
            <Button variant="outline" onClick={() => setShowUploadDialog(true)} data-testid="bulk-upload-btn">
              <Upload className="w-4 h-4 mr-2" />
              {t('store_codes.bulk_upload', 'Bulk Upload')}
            </Button>
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-code-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('store_codes.add_code', 'Add Code')}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{stats.total_codes}</p>
                <p className="text-sm text-gray-600">{t('store_codes.total_codes', 'Total Codes')}</p>
              </div>
            </CardContent>
          </Card>
          {Object.entries(stats.categories || {}).slice(0, 3).map(([cat, count]) => (
            <Card key={cat}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-600 truncate">{cat}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search and Filter */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder={t('store_codes.search_placeholder', 'Search by barcode, name, or brand...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[200px]" data-testid="category-filter">
                  <SelectValue placeholder={t('store_codes.all_categories', 'All Categories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('store_codes.all_categories', 'All Categories')}</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" variant="secondary">
                <Search className="w-4 h-4 mr-2" />
                {t('common.search', 'Search')}
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setSearchQuery(''); setFilterCategory(''); setPage(1); }}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8 text-gray-500">
                {t('common.loading', 'Loading...')}
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12">
                <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">
                  {t('store_codes.no_codes', 'No store codes found')}
                </p>
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('store_codes.add_first', 'Add Your First Code')}
                </Button>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allOnPageSelected}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Select all on page"
                          data-testid="select-all-codes"
                        />
                      </TableHead>
                      <TableHead>{t('store_codes.barcode', 'Barcode')}</TableHead>
                      <TableHead>{t('store_codes.product_name', 'Product Name')}</TableHead>
                      <TableHead>{t('store_codes.brand', 'Brand')}</TableHead>
                      <TableHead>{t('store_codes.category', 'Category')}</TableHead>
                      <TableHead>{t('store_codes.unit_size', 'Unit Size')}</TableHead>
                      <TableHead>{t('store_codes.discount', 'Discount')}</TableHead>
                      <TableHead className="text-right">{t('store_codes.actions', 'Actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow
                        key={entry.id}
                        data-testid={`code-row-${entry.barcode}`}
                        className={selectedBarcodes.has(entry.barcode) ? 'bg-blue-50' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedBarcodes.has(entry.barcode)}
                            onCheckedChange={() => toggleSelection(entry.barcode)}
                            aria-label={`Select ${entry.barcode}`}
                            data-testid={`select-code-${entry.barcode}`}
                          />
                        </TableCell>
                        <TableCell>
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                            {entry.barcode}
                          </code>
                        </TableCell>
                        <TableCell className="font-medium">{entry.product_name}</TableCell>
                        <TableCell className="text-gray-600">{entry.brand || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{entry.category}</Badge>
                        </TableCell>
                        <TableCell className="text-gray-600">{entry.unit_size || '-'}</TableCell>
                        <TableCell>
                          {entry.default_discount_tier ? (
                            <Badge className="bg-green-100 text-green-700">
                              {entry.default_discount_tier}%
                            </Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(entry)}
                              data-testid={`edit-btn-${entry.barcode}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(entry)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-btn-${entry.barcode}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      {t('common.previous', 'Previous')}
                    </Button>
                    <span className="py-2 px-4 text-sm text-gray-600">
                      {t('common.page', 'Page')} {page} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      {t('common.next', 'Next')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('store_codes.add_code', 'Add Store Code')}</DialogTitle>
              <DialogDescription>
                {t('store_codes.add_description', 'Add a proprietary barcode mapping for your store')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('store_codes.barcode', 'Barcode')} *</Label>
                <Input
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  placeholder="e.g., SKU001, WFM123456"
                  data-testid="barcode-input"
                />
              </div>
              <div>
                <Label>{t('store_codes.product_name', 'Product Name')} *</Label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                  placeholder="e.g., Organic Blueberries"
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label>{t('store_codes.brand', 'Brand')}</Label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  placeholder="e.g., 365 Everyday Value"
                />
              </div>
              <div>
                <Label>{t('store_codes.category', 'Category')} *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder={t('store_codes.select_category', 'Select category')} />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('store_codes.unit_size', 'Unit Size')}</Label>
                  <Input
                    value={formData.unit_size}
                    onChange={(e) => setFormData({ ...formData, unit_size: e.target.value })}
                    placeholder="e.g., 16 oz"
                  />
                </div>
                <div>
                  <Label>{t('store_codes.discount_tier', 'Default Discount')}</Label>
                  <Select 
                    value={formData.default_discount_tier?.toString() || 'none'} 
                    onValueChange={(v) => setFormData({ ...formData, default_discount_tier: v === 'none' ? null : parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select %" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {STORE_DISCOUNT_OPTIONS.map(tier => (
                        <SelectItem key={tier} value={tier.toString()}>{tier}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t('store_codes.notes', 'Notes')}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={t('store_codes.notes_placeholder', 'Optional notes about this product')}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleAddEntry} data-testid="save-code-btn">
                {t('common.save', 'Save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('store_codes.edit_code', 'Edit Store Code')}</DialogTitle>
              <DialogDescription>
                {t('store_codes.edit_description', 'Update the barcode mapping')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('store_codes.barcode', 'Barcode')}</Label>
                <Input value={formData.barcode} disabled className="bg-gray-50" />
              </div>
              <div>
                <Label>{t('store_codes.product_name', 'Product Name')} *</Label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('store_codes.brand', 'Brand')}</Label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('store_codes.category', 'Category')} *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('store_codes.unit_size', 'Unit Size')}</Label>
                  <Input
                    value={formData.unit_size}
                    onChange={(e) => setFormData({ ...formData, unit_size: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t('store_codes.discount_tier', 'Default Discount')}</Label>
                  <Select 
                    value={formData.default_discount_tier?.toString() || 'none'} 
                    onValueChange={(v) => setFormData({ ...formData, default_discount_tier: v === 'none' ? null : parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select %" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {STORE_DISCOUNT_OPTIONS.map(tier => (
                        <SelectItem key={tier} value={tier.toString()}>{tier}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t('store_codes.notes', 'Notes')}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowEditDialog(false); setSelectedEntry(null); resetForm(); }}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleEditEntry}>
                {t('common.save', 'Save Changes')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('store_codes.delete_confirm', 'Delete Store Code?')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('store_codes.delete_warning', 'This will permanently delete the store code mapping. This action cannot be undone.')}
                {selectedEntry && (
                  <div className="mt-2 p-2 bg-gray-100 rounded">
                    <code>{selectedEntry.barcode}</code> - {selectedEntry.product_name}
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteEntry} className="bg-red-600 hover:bg-red-700">
                {t('common.delete', 'Delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                {t('store_codes.bulk_upload', 'Bulk Upload Store Codes')}
              </DialogTitle>
              <DialogDescription>
                {t('store_codes.upload_description', 'Upload multiple store codes via CSV')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {t('store_codes.csv_format', 'Required columns: barcode, product_name, category')}
                </p>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  {t('store_codes.download_template', 'Download Template')}
                </Button>
              </div>
              <Textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={`barcode,product_name,brand,category,unit_size,default_discount_tier,notes
SKU001,Organic Blueberries,365,Fruits,6 oz,20,Fresh from local farms
SKU002,Whole Wheat Bread,Store Brand,Bakery & Bread,24 oz,15,`}
                rows={10}
                className="font-mono text-sm"
                data-testid="csv-input"
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="overwrite"
                  checked={overwriteDuplicates}
                  onChange={(e) => setOverwriteDuplicates(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="overwrite" className="text-sm">
                  {t('store_codes.overwrite_duplicates', 'Overwrite existing entries with same barcode')}
                </Label>
              </div>
              
              {uploadResult && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">{t('store_codes.upload_complete', 'Upload Complete')}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-green-600 font-bold">{uploadResult.created}</span> {t('store_codes.created', 'created')}
                    </div>
                    <div>
                      <span className="text-blue-600 font-bold">{uploadResult.updated}</span> {t('store_codes.updated', 'updated')}
                    </div>
                    <div>
                      <span className="text-gray-600 font-bold">{uploadResult.skipped}</span> {t('store_codes.skipped', 'skipped')}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowUploadDialog(false); setCsvText(''); setUploadResult(null); }}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={parseCsvAndUpload} data-testid="upload-csv-btn">
                <Upload className="w-4 h-4 mr-2" />
                {t('store_codes.upload', 'Upload')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Post Selected Codes as Live Deals */}
        <BulkPostModal
          open={showBulkPost}
          onOpenChange={setShowBulkPost}
          selectedProducts={getSelectedProducts()}
          source="store_registry"
          onSuccess={handleBulkPostSuccess}
        />
      </div>
    </RetailerLayout>
  );
}
